import { useState } from 'react';
import { Printer, BluetoothConnected, Usb, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { printerService, type PrinterStatus } from '../../lib/print/printerService';

interface PrinterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PrinterDrawer({ isOpen, onClose }: PrinterDrawerProps) {
  const [status, setStatus] = useState<PrinterStatus>(() => printerService.getStatus());
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleConnectSerial = async () => {
    setConnecting(true);
    setError(null);
    try {
      await printerService.connectSerial();
      setStatus(printerService.getStatus());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect USB/Serial printer.');
    } finally {
      setConnecting(false);
    }
  };

  const handleConnectBluetooth = async () => {
    setConnecting(true);
    setError(null);
    try {
      await printerService.connectBluetooth();
      setStatus(printerService.getStatus());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect Bluetooth printer.');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    await printerService.disconnect();
    setStatus(printerService.getStatus());
  };

  const handleTestKick = async () => {
    await printerService.kickDrawer();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl w-full max-w-md p-5 space-y-5 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-emerald-600" />
            <h3 className="font-bold text-slate-900 dark:text-white text-sm">POS Hardware & Printer Setup</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-900 dark:text-white">Current Connection</p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {status.connected ? status.deviceName : 'Using standard system window print (Browser default)'}
            </p>
          </div>
          {status.connected ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-bold text-[11px]">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300 font-bold text-[11px]">
              Standby
            </span>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300 text-xs flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-3">
          <button
            type="button"
            onClick={handleConnectSerial}
            disabled={connecting}
            className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-left flex items-center justify-between transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-emerald-600">
                <Usb className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-xs text-slate-900 dark:text-white">Connect USB / Serial Thermal Printer</p>
                <p className="text-[11px] text-slate-500">ESC/POS thermal printers connected via USB/Serial port</p>
              </div>
            </div>
            <span className="text-xs font-bold text-emerald-600">Connect</span>
          </button>

          <button
            type="button"
            onClick={handleConnectBluetooth}
            disabled={connecting}
            className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-left flex items-center justify-between transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950 text-blue-600">
                <BluetoothConnected className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-xs text-slate-900 dark:text-white">Connect Bluetooth Thermal Printer</p>
                <p className="text-[11px] text-slate-500">Wireless Bluetooth receipt printers and card readers</p>
              </div>
            </div>
            <span className="text-xs font-bold text-blue-600">Pair</span>
          </button>
        </div>

        <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <button
            type="button"
            onClick={handleTestKick}
            className="px-3.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
          >
            Test Cash Drawer Kick
          </button>

          {status.connected && (
            <button
              type="button"
              onClick={handleDisconnect}
              className="px-3.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
